import { RadarChartOutlined } from '@ant-design/icons';
import { modal } from '@repo/ui';
import { AutomationExecutionNodeStatusEnum, AutomationStatus } from '@repo/utils';
import { type Node, type NodeProps } from '@xyflow/react';
import { Card, Drawer } from 'antd';
import { NodeType } from 'automations/data/nodeTypes';
import { useAutomationDispatch } from 'automations/hooks/useAutomationDispatch';
import useCommonNodeHandles from 'automations/hooks/useCommonNodeHandles';
import { automationStatusSelector } from 'automations/selectors/automationConfig';
import { childrenCountSelector } from 'automations/selectors/reactFlow';
import { addNodeWithPosition } from 'automations/store/reactFlowSlice';
import { deleteNode } from 'automations/utils/deleteNode';
import { generateNewNodePayload } from 'automations/utils/node';
import { useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';

import NodeUi from '../NodeUi';

function AdditionalUI({
  open,
  setMenuOpen,
  data,
}: {
  open: boolean;
  setMenuOpen: (open: boolean) => void;
  data: any;
}) {
  return (
    <Drawer title="Node Data" size="large" open={open} onClose={() => setMenuOpen(false)}>
      <Card>
        <pre>{JSON.stringify(data, null, 2)}</pre>
      </Card>
    </Drawer>
  );
}

export default function FallbackNode(node: NodeProps<Node<any>>) {
  const { open, setMenuOpen } = useCommonNodeHandles(node);
  const { id, data } = node;

  const dispatch = useAutomationDispatch();
  const childrenCount = useSelector(childrenCountSelector(id));

  const automationStatus = useSelector(automationStatusSelector);
  const targetExecStatus = node?.data?.executionStatus;

  const backgroundStatus = useMemo(() => {
    if (targetExecStatus === AutomationExecutionNodeStatusEnum.SUCCESS) return 'success';
    if (targetExecStatus === AutomationExecutionNodeStatusEnum.FAILED) return 'failed';
    return undefined;
  }, [targetExecStatus]);

  const handleDeleteNode = useCallback(() => {
    modal.confirm({
      title: 'Are you sure you want to delete this node?',
      content: 'NOTE: This action cannot be undone, All child nodes of this node will also be deleted.',
      centered: true,
      width: 600,
      onOk: () => dispatch(deleteNode(id)),
      okText: 'Delete',
      okButtonProps: {
        danger: true,
      },
    });
  }, [dispatch, id]);

  const handleAddNode = useCallback(() => {
    const newNode = generateNewNodePayload({
      nodeType: NodeType.newBlock,
      data: {
        executionOrder: childrenCount + 1,
      },
    });
    dispatch(addNodeWithPosition({
      newNode,
      sourceNodeId: id,
    }));
  }, [childrenCount, dispatch, id]);

  const canDeleteNode = automationStatus === AutomationStatus.draft;

  return (
    <NodeUi
      nodeId={id}
      nodeTitle={`Fallback Node (${data.nodeSubType})`}
      nodeSubtitle={data?.description ?? 'This is a Custom Node, Click to view Node Data'}
      icon={{
        iconContent: <RadarChartOutlined />,
      }}
      hasErrors={false}
      errorMessages={[]}
      sourceHandle={{}}
      targetHandle={{
        onClick: handleAddNode,
      }}
      contextMenu={{
        onOpenDetails: () => setMenuOpen(true),
        onDelete: canDeleteNode ? handleDeleteNode : undefined,
      }}
      AdditionalUI={<AdditionalUI open={open} setMenuOpen={setMenuOpen} data={data} />}
      debugContent={data}
      successExecutionCounts={data?.successExecutionCounts}
      backgroundStatus={backgroundStatus}
    />
  );
}
